import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import fontSelectors from "./../../../redux/fonts/selectors";
import { show as showFont } from "../../../redux/fonts";
import { useSsrEffect } from "@issr/core";

const Tspan = (props, context) => {
  const { changeComputedLineSize, line, lines, asPath, lineNum } =
    props;

  const { loadedFont, loadingFont } = useSelector((state) => ({
    loadedFont: fontSelectors.selectFontForSvg(
      state.fonts.list,
      props.line.font
    ),
    loadingFont: state.fonts.loadingShow,
  }));

  const dispatch = useDispatch();

  /** frontload async calls */
  const frontload = async () => {
    let loadStyle = false;
    if (!loadingFont && loadedFont.styles) {
      loadedFont.styles.forEach((style) => {
        if (style.available && !style.loaded) {
          loadStyle = true;
        }
      });
      if (loadStyle) {
        dispatch(showFont({ id: loadedFont.id }));
      }
    }
  };

  useSsrEffect(frontload, [loadedFont, showFont, loadingFont]);
  const lineRef = useRef(null);

  const scaleContentToPreview = () => {
    // console.log("[PERFORMANCE]", "scaleContentToPreview");
    // console.log('==== UPDATING SIZE TSPAN ====', line.id);
    const lineSvg = lineRef.current;
    const bbox = loadedFont && lineSvg ? lineSvg.getBBox() : { height: 0 };
    if (bbox.height === 0) {
      return;
    }
    // console.log('scale content', line.id, bbox.height, line.computedLineHeight);

    if (
      bbox.height !== line.computedLineHeight ||
      bbox.width !== line.computedLineWidth
    ) {
      changeComputedLineSize(lineNum - 1, bbox.height, bbox.width);
    }
  };
  useEffect(scaleContentToPreview, [
    lineRef,
    loadedFont,
    line,
    lineNum,
    changeComputedLineSize,
  ]);

  const safelyParseSvg = (styleToLoad) => {
    try {
      return JSON.parse(styleToLoad.svg);
    } catch (e) {
      console.log(e);
      return false; // error in the above string (in this case, yes)!
    }
  };

  const renderTextToPath = () => {
    // console.log("[PERFORMANCE]", "render TextToPath");
    if (loadedFont.styles) {
      // FIXME Load font according to line font file.
      // console.log('line', line.font.fontFamily);
      let font = false;
      // get font style
      let fontStyle = "regular";
      if (line.font.bold) {
        if (line.font.italic) {
          fontStyle = "italic_bold";
        } else {
          fontStyle = "bold";
        }
      } else {
        if (line.font.italic) {
          fontStyle = "italic";
        }
      }
      let styleToLoad = false;
      loadedFont.styles.forEach((style) => {
        if (style.title === fontStyle && style.loaded) styleToLoad = style;
      });

      // failsafe. if bold or italic not available we take regular (0)
      if (!styleToLoad) {
        styleToLoad = loadedFont.styles[0];
      }

      if (styleToLoad.loaded) {
        font = safelyParseSvg(styleToLoad);
      }

      // console.log(fontStyle);

      let shift = 0;
      let path = "";
      let notfirst = false;
      const letterSpacing = 0;
      const letters = line && line.text && line.text.split("");
      if (font && letters) {
        letters.forEach((letter, inc) => {
          const letterGraph = font.glyphs[letter];
          const prev = (notfirst && font.glyphs[letters[inc - 1]]) || {};
          // console.log(letter, notfirst, font.glyphs[letters[inc - 1]], inc, prev.w, font.w, prev.k, letterSpacing, (prev.k && prev.k[letters[inc]] || 0));
          shift += notfirst
            ? (prev.w || font.w) +
              (prev.k && prev.k[letters[inc]] ? prev.k[letters[inc]] : 0) +
              (prev.w || font.w) * letterSpacing
            : 0;
          notfirst = true;
          // console.log('shift', shift);
          if (letterGraph && letterGraph.d) {
            let ldParam =
              "M" +
              letterGraph.d.replace(/[mlcxtrv]/g, (command) => {
                return (
                  { l: "L", c: "C", x: "z", t: "m", r: "l", v: "c" }[command] ||
                  "M"
                );
              }) +
              " z";
            // console.log(ldParam);
            ldParam = ldParam.replace(/M[-\d{1,5}]+/g, (command) => {
              const MM = command.replace("M", "").split(",");
              // console.log('[DEBUG]', (parseInt(MM[0], 10) + shift), shift, letter);
              return (
                "M " +
                (parseInt(MM[0], 10) + shift) +
                (typeof MM[1] !== "undefined" && MM[1] !== ""
                  ? " " + MM[1]
                  : " ") +
                " "
              );
            });

            path += ldParam;
          }
        });

        // Calculate Position of Line based on all Lines above
        const lineSvg = lineRef.current;
        let computedPositionY = 0;
        let computedPositionX = 0;
        if (lineSvg) {
          const lineBBox = lineSvg.getBBox();
          computedPositionY += Math.abs(lineBBox.y);

          // This one removes the position to the left FIX
          computedPositionX = lineBBox.x * -1;

          let longestComputedLineWidth = 0;
          if (lines.length > 1) {
            lines.forEach((ll, idx) => {
              if (idx < lineNum - 1) {
                computedPositionY +=
                  ll.computedLineHeight * ll.lineHeight * 1.05;
              }
              if (ll.computedLineWidth > longestComputedLineWidth) {
                longestComputedLineWidth = ll.computedLineWidth;
              }
            });

            switch (line.textAnchor) {
              case "end":
                computedPositionX += longestComputedLineWidth - lineBBox.width;
                break;
              case "middle":
                computedPositionX +=
                  (longestComputedLineWidth - lineBBox.width) / 2;
                break;
              default:
                break;
            }
          }
        }

        // normalize font size
        const scaleFactor = line.font.fontSize / font.face["units-per-em"];

        return (
          <g
            transform={
              "translate(" + computedPositionX + "," + computedPositionY + ")"
            }
            fill={line.color.hex}
            stroke="none"
            key={"svg-tspan-line_" + lineNum}
            ref={lineRef}
          >
            <path transform={"scale(" + scaleFactor + ")"} d={path}></path>
          </g>
        );
      } else {
        // eslint-disable-line no-else-return
        return <rect />;
      }
    } else {
      // eslint-disable-line no-else-return
      return <rect />;
    }
  };

  // console.log("[PERFORMANCE]", "render Line / tspan");
  if (asPath) {
    return renderTextToPath();
  } else {
    if (line.font) {
      // FIXME this if is just for development of new editor
      line.font = {
        fontSize: line.font.fontSize || 16,
        bold: line.font.bold || false,
        italic: line.font.italic || false,
        color: {
          hex:
            line.font && line.font.color && line.font.color.hex
              ? line.font.color.hex
              : "#000",
        },
      };
    } else {
      line.font = {
        fontSize: 16,
        bold: false,
        italic: false,
        color: {
          hex: "#000", //((line.font && line.font.color && line.font.color.hex) ?  line.font.color.hex : '#000')
        },
      };
    }
    line.lineHeight = line.lineHeight || 1;
    line.text = line.text || "This is just a test";
    const dy = line.font.fontSize * line.lineHeight * 1.05;
    const y = lineNum * 20; // FIXME just for tspan workaround

    return (
      <tspan
        x="0"
        y={y}
        fontWeight={line.font.bold ? "bold" : "normal"}
        fontStyle={line.font.italic ? "italic" : "normal"}
        key={"svg-tspan-line_" + lineNum}
        ref={lineRef}
        fontSize={line.font.fontSize}
        fontFamily={line.font.title}
        fill={line.color ? line.color.hex : "#000"}
        textAnchor={line.textAnchor}
        dy={dy}
      >
        {line.text} x
      </tspan>
    );
  }
};

/**  define proptype for the 'translation' function  */
Tspan.contextTypes = {
  t: PropTypes.func,
};

/** export the component as redux connected component */
export default Tspan;
